import { useEffect, useState } from 'react';

import {
  Home,
  FolderOpen,
  Users,
  FileCheck,
  Receipt,
  FileText,
  ShieldCheck,
  UserCircle,
  Hospital,
  ShieldUser,
  Layers,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';

import { ROUTES, RESTRICTED_ROUTES } from '@/router/routes';
import { useUserProfile } from '@/states/user-profile';
import { ROLES } from '@/utils/constants';

export type NavItem = {
  label_translation_key: string;
  icon: LucideIcon;
  href?: string;
  subItems?: NavItem[];
  callback?: () => void;
};

const getMainNavItems = (role?: ROLES) => {
  const nav_links: NavItem[] = [
    { label_translation_key: 'nav-dashboard', href: ROUTES.DASHBOARD, icon: Home },
    {
      label_translation_key: 'nav-admin',
      icon: ShieldCheck,
      subItems: [
        { label_translation_key: 'nav-admin-users', href: ROUTES.ADMIN_USERS, icon: Users },
        { label_translation_key: 'nav-admin-clinics', href: ROUTES.ADMIN_CLINICS, icon: Hospital },
        {
          label_translation_key: 'nav-admin-telehealth-clinicians',
          href: ROUTES.ADMIN_TELEHEALTH_CLINICIANS,
          icon: ShieldUser,
        },
        {
          label_translation_key: 'nav-admin-ai-search',
          href: ROUTES.ADMIN_AI_SEARCH,
          icon: FileText,
        },
      ],
    },
    { label_translation_key: 'nav-cases', href: ROUTES.CASES, icon: FolderOpen },
    { label_translation_key: 'nav-users', href: ROUTES.USERS, icon: Users },
    { label_translation_key: 'nav-grading', href: ROUTES.GRADING, icon: FileCheck },
    { label_translation_key: 'nav-insights', href: ROUTES.INSIGHTS, icon: Layers },
    { label_translation_key: 'nav-sales', href: ROUTES.SALES, icon: TrendingUp },
    { label_translation_key: 'nav-billing', href: ROUTES.BILLING, icon: Receipt },
    { label_translation_key: 'nav-profile', href: ROUTES.PROFILE, icon: UserCircle },
    { label_translation_key: 'nav-legal', href: ROUTES.LEGAL, icon: FileText },
  ].filter((navItem) => {
    if (!role) return true;

    const allowed = RESTRICTED_ROUTES[role];
    if (!allowed) return true;

    if (navItem.href) {
      return allowed.includes(navItem.href);
    }

    if (navItem.subItems) {
      return navItem.subItems.some((subItem) => allowed.includes(subItem.href ?? ''));
    }

    return false;
  });

  return nav_links;
};

export const useNavigationItems = () => {
  const { profile } = useUserProfile();
  const [navItems, setNavItems] = useState<NavItem[]>(getMainNavItems(profile.role));

  useEffect(() => {
    if (!profile.role) return;
    setNavItems(getMainNavItems(profile.role));
  }, [profile.role]);

  return { navItems };
};
